import React from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Badge } from "@components/Badge/Badge";
import { Button } from "@components/Button";
import { parseHtml } from "@components/HTMLParser/utils";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useFormatPrice } from "@hooks/useFormatPrice";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { useJointBillServiceProviderBillPlansQuery } from "@services/jointBillServiceProviderBillPlans/queries";
import { isNullOrUndefined } from "@utils/common";
import { DEFAULT_PLAN_DISCOUNT_DURATION } from "@utils/plans/constants";
import { type IPlanListItem } from "@utils/plans/types";
import { formatDate, formatPrice } from "@utils/units";
import { type TCurrency } from "@/types";
import { PlanCardListItem } from "./PlanCardListItem";

export interface IPlanCardProps extends IPlanListItem {
  price: number;
  discountedPrice?: number;
  discountedMonths?: number;
  discountExpirationDate?: Date;
  isSelected?: boolean;
  isMutationPending?: boolean;
  className?: string;
  jointBillServiceId: number;
  onOrderClick: () => void;
  headerClassName?: string;
  customButtonText?: string;
  currency?: TCurrency;
  isPlanChangeAvailable?: boolean;
}

export const PlanCard: React.FC<IPlanCardProps> = (props) => {
  const {
    className,
    price,
    discountedPrice,
    discountedMonths,
    discountExpirationDate,
    isSelected,
    jointBillServiceId,
    onOrderClick,
    isMutationPending,
    featureKey,
    mainFeatureKey,
    name,
    subtitle,
    isMostPopular,
    isPlanGroup,
    afterPriceTitle,
    headerClassName,
    customButtonText,
    isPlanChangeAvailable,
  } = props;

  const { t } = useTranslation();

  const { isLoading: isPlansLoading } =
    useJointBillServiceProviderBillPlansQuery(jointBillServiceId);

  const { isDefaultPlanDiscountEnabled } = useFeatureConfig();

  const subtitleText = parseHtml(t(subtitle ?? ""));

  const featureTranslations = useTranslationArray(featureKey ?? "");
  const mainFeatureTranslations = useTranslationArray(mainFeatureKey ?? "");

  const formattedPrice = useFormatPrice(price);
  const formattedDiscountedPrice = useFormatPrice(discountedPrice ?? price);

  return (
    <div
      className={twMerge(
        "flex flex-col gap-6 divide-y divide-grey-light rounded-xl border border-grey-light bg-base-white px-[22px] py-8 md:pb-11",
        className,
      )}
    >
      <div className="relative flex min-h-[256px] flex-col">
        <div className="flex flex-col">
          <div className="flex justify-between">
            <h2
              className={twMerge(
                "mb-4 text-2xl font-semibold",
                headerClassName,
              )}
            >
              {t(name)}
            </h2>
            {isMostPopular && (
              <Badge
                className="bg-purple-hover px-3 py-1"
                size="medium"
                color="purple"
                variant="bubble"
              >
                {t("plan.card.mostPopular")}
              </Badge>
            )}
          </div>
          {subtitleText && (
            <h6 className="pt-1 font-normal text-grey-dark">{subtitleText}</h6>
          )}
        </div>
        <div className="flex flex-grow flex-col justify-end">
          <div className="pb-5">
            {isPlanGroup && (
              <h5 className="mb-1 text-sm font-normal leading-5 text-grey-dark">
                {t("plan.card.from")}
              </h5>
            )}
            <h1
              className={twMerge(
                "p-0 !text-2xl font-bold",
                !isNullOrUndefined(discountedPrice) &&
                  "!text-lg font-normal line-through",
              )}
            >
              <span>{formattedPrice}</span>
              <span className="text-lg !leading-8">
                {t("plan.card./month")}
              </span>
            </h1>
            {!isNullOrUndefined(discountedPrice) && (
              <h1 className="p-0 !text-2xl font-bold text-base-green">
                <span>{formattedDiscountedPrice}</span>
                <span className="text-lg !leading-8">
                  {t("plan.card./month")}
                </span>
              </h1>
            )}
            {afterPriceTitle && (
              <h6 className="mt-2 text-sm font-normal text-grey-dark">
                {t(afterPriceTitle)}
              </h6>
            )}
          </div>
          <Button
            className={twMerge("w-full", !isPlanChangeAvailable && "hidden")}
            onClick={onOrderClick}
            disabled={isPlansLoading || isSelected}
            isLoading={isMutationPending}
          >
            {customButtonText ??
              (isSelected ? t("plan.button.yourPlan") : t("plan.button.order"))}
          </Button>
        </div>
      </div>
      <div className="pt-6">
        {mainFeatureKey && mainFeatureTranslations && (
          <ul className="list-none p-0">
            {mainFeatureTranslations.map((feature) => (
              <PlanCardListItem key={feature}>
                {parseHtml(feature)}
              </PlanCardListItem>
            ))}
          </ul>
        )}
        {featureKey && featureTranslations && (
          <ul className="list-none p-0">
            {discountExpirationDate && (
              <PlanCardListItem key={`${name}-discount-expiration-date`}>
                <span className="font-bold">
                  {parseHtml(
                    t("plan.card.commonFeature.discountExpirationDate", {
                      discountExpirationDate: formatDate(
                        discountExpirationDate,
                        t("common.dateFormat.numericalFullDate").split(" ")[0],
                      ),
                      discountedPrice: formatPrice(discountedPrice ?? 0),
                    }),
                  )}
                </span>
              </PlanCardListItem>
            )}
            {(isDefaultPlanDiscountEnabled || discountedMonths) && (
              <PlanCardListItem key={`${name}-discount-feature`}>
                <span
                  className={twMerge(!discountExpirationDate && "font-bold")}
                >
                  {parseHtml(
                    t("plan.card.commonFeature.discount", {
                      discountedMonths:
                        discountedMonths ?? DEFAULT_PLAN_DISCOUNT_DURATION,
                    }),
                  )}
                </span>
              </PlanCardListItem>
            )}
            {featureTranslations.map((feature) => (
              <PlanCardListItem key={feature}>
                {parseHtml(feature)}
              </PlanCardListItem>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
